import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ChevronUp,
  Calendar,
  Clock,
  User,
  ChevronDown,
  Eye,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../App";
import moment from "moment";

export const PreviousSessionsSection = ({
  expanded,
  onToggle,
  previousSessions,
  onUpdateStatus,
  expandedSessionId,
  onSessionToggle,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { user } = useAuth();

  const handleSessionClick = (sessionId) => {
    // navigate(`/sessions/${sessionId}`); // Legacy navigation
    if (onSessionToggle) {
      onSessionToggle(sessionId);
    }
  };

  const getStatusGradient = (status) => {
    switch (status) {
      case "completed":
        return "from-green-500 to-green-600";
      case "cancelled":
        return "from-gray-500 to-gray-600";
      case "no-show":
        return "from-red-500 to-red-600";
      default:
        return "from-blue-500 to-blue-600";
    }
  };

  const formatSessionDate = (dateString) => {
    return moment(dateString).format("MMM D, YYYY - h:mm A");
  };

  return (
    <div
      className={`${colors.cardBg} backdrop-blur-sm rounded-2xl ${colors.cardBorder} shadow-lg overflow-hidden`}
    >
      <div
        className={`flex items-center justify-between p-6 cursor-pointer ${colors.tableRowHover} transition-colors`}
        onClick={onToggle}
      >
        <h2
          className={`text-lg font-bold ${colors.textPrimary} flex items-center`}
        >
          <Calendar className={`w-5 h-5 mr-2 ${colors.textSecondary}`} />
          {t('sessionDetails.previous.title')}
        </h2>
        <div className="flex items-center space-x-3">
          <span
            className={`px-3 py-1 ${colors.inputBg} rounded-full text-xs font-semibold ${colors.textSecondary}`}
          >
            {t('sessionDetails.previous.count', { count: previousSessions?.length || 0 })}
          </span>
          {expanded ? (
            <ChevronUp className={`w-5 h-5 ${colors.textTertiary}`} />
          ) : (
            <ChevronDown className={`w-5 h-5 ${colors.textTertiary}`} />
          )}
        </div>
      </div>

      {expanded && (
        <div className={`border-t ${colors.tableBorder}`}>
          <div className="p-6 space-y-3">
            {/* Render actual previous sessions */}
            {previousSessions && previousSessions.length > 0 ? (
              previousSessions.map((session) => {
                const isExpanded = expandedSessionId === session.id;
                const isStatusScheduled = (session.status || "scheduled") === "scheduled";
                const isDisabledForTrainer = user?.role === 'trainer' && !isStatusScheduled;

                return (
                  <div
                    key={session.id || session.SK || Math.random()}
                    className={`border ${isExpanded ? 'border-blue-400/30 ring-1 ring-blue-400/20' : colors.inputBorder} ${colors.inputBg} rounded-2xl overflow-hidden transition-all duration-200`}
                  >
                    <div
                      onClick={() => handleSessionClick(session.id || session.sessionId || session.SK)}
                      className={`p-5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4">

                        {/* 1. Date & Time Block */}
                        <div className="flex items-center gap-4 min-w-0 sm:min-w-[200px]">
                          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${colors.inputBg} border ${colors.inputBorder} text-blue-500 shadow-sm shrink-0`}>
                            <Calendar className="w-6 h-6" />
                          </div>
                          <div>
                            <div className={`font-bold ${colors.textPrimary} text-base`}>
                              {moment(session.startTime).format("D MMMM YYYY")}
                            </div>
                            <div className={`text-sm ${colors.textSecondary} flex items-center gap-1.5 mt-0.5`}>
                              <Clock className="w-3.5 h-3.5" />
                              {moment(session.startTime).format("HH:mm")}
                            </div>
                          </div>
                        </div>

                        {/* 2. Info Block (Package & Trainer) */}
                        <div className="flex-1 flex flex-col justify-center gap-2 sm:gap-1.5 min-w-0 py-2 sm:py-0 border-t sm:border-t-0 border-dashed border-gray-200 dark:border-gray-700 sm:border-l sm:pl-6">
                          {/* Package */}
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0`} />
                            <span className={`text-sm ${colors.textSecondary} truncate font-medium`}>
                              {session.packageName || t('sessionDetails.previous.na')}
                            </span>
                          </div>

                          {/* Trainer */}
                          {session.trainerName && (
                            <div className="flex items-center gap-2 min-w-0">
                              <User className={`w-3.5 h-3.5 ${colors.textTertiary} shrink-0`} />
                              <span className={`text-sm ${colors.textPrimary} truncate`}>
                                {session.trainerName}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* 3. Actions & Status Block */}
                        <div className="flex items-center justify-between sm:justify-end gap-3 pt-2 sm:pt-0">
                          {/* Status Select */}
                          <div
                            className="relative"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <select
                              value={session.status}
                              disabled={isDisabledForTrainer}
                              onChange={(e) =>
                                onUpdateStatus &&
                                onUpdateStatus(session.id, e.target.value)
                              }
                              className={`appearance-none pl-3 pr-8 py-2 bg-gradient-to-r ${getStatusGradient(session.status)} text-white rounded-xl text-xs font-bold shadow-sm cursor-point transition-all hover:opacity-90 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed`}
                            >
                              <option value="scheduled" className="bg-blue-600 text-white">{t('sessionDetails.status.scheduled')}</option>
                              <option value="completed" className="bg-green-600 text-white">{t('sessionDetails.status.completed')}</option>
                              <option value="cancelled" className="bg-gray-600 text-white">{t('sessionDetails.status.cancelled')}</option>
                              <option value="no-show" className="bg-red-600 text-white">{t('sessionDetails.status.noShow')}</option>
                            </select>
                            <ChevronDown
                              className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white pointer-events-none`}
                            />
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/sessions/${session.id || session.sessionId || session.SK}`);
                              }}
                              className={`p-2 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 border border-blue-200 dark:border-blue-800 transition-all shadow-sm hover:shadow active:scale-95`}
                              title={t('common.viewDetails')}
                            >
                              <Eye className="w-5 h-5" />
                            </button>

                            <div className={`p-2 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-all shadow-sm ${isExpanded ? 'bg-gray-100 dark:bg-gray-700' : ''}`}>
                              {isExpanded ? (
                                <ChevronUp className={`w-5 h-5 ${colors.textSecondary}`} />
                              ) : (
                                <ChevronDown className={`w-5 h-5 ${colors.textMuted} transition-colors`} />
                              )}
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>

                    {/* Expanded Exercises Content */}
                    {
                      isExpanded && (
                        <div className={`px-4 pb-4 bg-gray-50/50 dark:bg-gray-800/10 border-t ${colors.tableBorder}`}>
                          <div className="mt-4">
                            {/* Table Header (desktop only) */}
                            <div className="hidden sm:grid grid-cols-12 gap-4 mb-2 px-2 text-xs font-semibold uppercase tracking-wider opacity-60">
                              <div className="col-span-4">{t('sessionDetails.training.exercise')}</div>
                              <div className="col-span-2 text-center">{t('sessionDetails.training.kg')}</div>
                              <div className="col-span-2 text-center">{t('sessionDetails.training.reps')}</div>
                              <div className="col-span-2 text-center">{t('sessionDetails.training.sets')}</div>
                              <div className="col-span-2">{t('sessionDetails.training.notes')}</div>
                            </div>

                            {/* Exercises List */}
                            {session.exercises && session.exercises.length > 0 ? (
                              <div className="space-y-2">
                                {session.exercises.map((ex, idx) => (
                                  <div key={idx} className={`p-2.5 sm:p-2 rounded-lg text-sm ${colors.inputBg} border ${colors.inputBorder} sm:grid sm:grid-cols-12 sm:gap-4`}>
                                    <div className={`sm:col-span-4 font-medium ${colors.textPrimary} truncate`}>
                                      {ex.exerciseName || ex.name || "-"}
                                    </div>
                                    {/* Mobile: labeled inline values / Desktop: grid cells */}
                                    <div className={`sm:col-span-2 sm:text-center ${colors.textSecondary} mt-1 sm:mt-0 inline-block sm:block mr-3 sm:mr-0 text-xs sm:text-sm`}>
                                      <span className="sm:hidden opacity-60">{t('sessionDetails.training.kg')}: </span>
                                      {ex.weight || "-"}
                                    </div>
                                    <div className={`sm:col-span-2 sm:text-center ${colors.textSecondary} mt-1 sm:mt-0 inline-block sm:block mr-3 sm:mr-0 text-xs sm:text-sm`}>
                                      <span className="sm:hidden opacity-60">{t('sessionDetails.training.reps')}: </span>
                                      {ex.reps || "-"}
                                    </div>
                                    <div className={`sm:col-span-2 sm:text-center ${colors.textSecondary} mt-1 sm:mt-0 inline-block sm:block mr-3 sm:mr-0 text-xs sm:text-sm`}>
                                      <span className="sm:hidden opacity-60">{t('sessionDetails.training.sets')}: </span>
                                      {ex.sets || "-"}
                                    </div>
                                    <div className={`sm:col-span-2 ${colors.textSecondary} truncate mt-1 sm:mt-0 text-xs sm:text-sm`}>
                                      <span className="sm:hidden opacity-60">{t('sessionDetails.training.notes')}: </span>
                                      {ex.notes || "-"}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-6 text-gray-500 text-sm italic">
                                {t('sessionDetails.previous.noActivities')}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    }
                  </div>
                )
              })
            ) : (
              /* If no previous sessions */
              <div className="text-center py-8">
                <div
                  className={`w-16 h-16 ${colors.emptyStateBg} rounded-full mx-auto mb-4 flex items-center justify-center`}
                >
                  <Calendar className={`w-8 h-8 ${colors.emptyStateIcon}`} />
                </div>
                <p className={`${colors.textMuted} text-sm`}>
                  {t('sessionDetails.previous.empty')}
                </p>
              </div>
            )}
          </div>
        </div>
      )
      }
    </div >
  );
};

